// Full-document render of the routable stearns ProductLinePage: PageSkeleton
// (the React document shell) wrapping the legacy product_line_page.html body
// block -- the product hero (hero assembly + modular configurator) and the
// consolidated PLP body -- with the same container/section DOM the SCSS anchors
// to (#product-detail-container, .product-hero, .product-hero__blocks,
// .product-hero__configurator, and the inner <main>). The iovation scripts are
// page-scoped here (legacy included tsicommon/reviews/iovation.html only on
// this page); they are injected imperatively from a post-hydration effect
// (IovationScripts) to keep the IGLOO -> snare -> loader execution order under
// React 19. Identical to
// ProductDisplayPageTemplate -- the two share the body assembly and the hero
// chrome; they diverge only server-side (body_class + JSON-LD product source).
import React from "react";
import { IovationScripts } from "tsi-common-react/src/components/IovationScripts/index";

import { Context, templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { ProductLinePageBody } from "../components/ProductLinePageBody";
import { Template as ModularConfigurator } from "./ModularConfiguratorTemplate";
import { Template as ProductHeroAssembly } from "./ProductHeroAssemblyTemplate";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader/RichNavigation chain via PageSkeleton, which is typed with
// mutable reactivated props. The body/hero/configurator islands accept Immutable
// props, to which these are assignable.
export const Template = (
    props: templates.ProductLinePageTemplate,
): React.JSX.Element => {
    const chrome = React.useContext(Context).chrome;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div id="product-detail-container">
                {/* This inner <main> is load-bearing: #product-detail-container
                    is an align-items:center flex column, and _pdp.scss's
                    `#product-detail-container main` rules give the main
                    width:100% at >=1440px -- without it the hero/body
                    shrink-wrap and center at wide viewports. (Nested <main>s
                    match the legacy shell, whose #main-content wrapped this
                    template's own <main>.) */}
                <main>
                    <section className="product-hero">
                        <div className="product-hero__blocks">
                            <ProductHeroAssembly {...props.hero} />
                        </div>
                        {/* The legacy class list appended
                            `{{ page.configurator_background_style }}`, which
                            the stearns PLP model does not define -- it always
                            rendered empty. */}
                        <div className="product-hero__configurator">
                            <ModularConfigurator {...props.configurator} />
                        </div>
                    </section>
                    {/* The plain div stands where the legacy body island's host
                        div sat, keeping main's child list section + single div:
                        the sibling-sensitive `main > div + div` rule stays
                        inert and the `main > div .product-callout-block`
                        responsive-visibility rules keep matching, exactly as in
                        the island-era render. */}
                    <div>
                        <ProductLinePageBody
                            page={props.page}
                            reviews={props.reviews}
                            change_root_ssr={props.change_root_ssr}
                            root_conditional_ssr={props.root_conditional_ssr}
                        />
                    </div>
                </main>
            </div>
            <IovationScripts
                iovationUrl={props.iovation_url}
                staticBase={chrome.static_base}
            />
        </PageSkeleton>
    );
};
